<template lang="pug">
article.person-card
  nuxt-link.person-image-link(:to='localePath(person.path)', :aria-label='formatFullName(person)')
    img.person-image(
      v-if='personImage'
      :src='personImage'
      :alt='formatFullName(person)'
      loading='lazy'
      decoding='async'
    )
    base-image-placeholder.person-image-placeholder(v-else)
  .person-copy
    p.person-role {{ person.en.role }}
    h3.person-name
      nuxt-link(:to='localePath(person.path)') {{ formatFullName(person) }}
    p.person-room(v-if='showLocation') {{ t('person.room', { room: person.room }) }}
    .person-meta(v-if='showContactDetails')
      p(v-if='person.phone')
        v-icon(size='18') mdi-phone
        a(:href='`tel:${person.phone}`') {{ person.phone }}
      p(v-if='person.email')
        v-icon(size='18') mdi-email
        a(:href='`mailto:${person.email}`') {{ person.email }}
</template>

<script setup>
import { publicAssetPath } from '~/utils/assets'
import { formatFullName } from '~/utils/person'

defineOptions({
  name: 'PersonCard',
})

const props = defineProps({
  person: {
    type: Object,
    default: () => ({}),
  },
})

const { t } = useI18n()
const localePath = useLocalePath()

const personImage = computed(() => {
  if (!props.person.picture) return ''
  return publicAssetPath(props.person.picture)
})
const isExternalMember = computed(() => !!props.person.external)
const showLocation = computed(
  () => !isExternalMember.value && !!props.person.room,
)
const showContactDetails = computed(() => !isExternalMember.value)
</script>

<style scoped>
.person-card {
  display: grid;
  grid-template-columns: minmax(10rem, 12rem) minmax(0, 1fr);
  min-width: 0;
  height: 100%;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
  overflow: hidden;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.person-card:hover {
  transform: translateY(-2px);
  border-color: var(--border-strong);
  box-shadow: var(--shadow-soft);
}

.person-image-link {
  display: block;
  height: 100%;
  min-width: 0;
  color: inherit;
  overflow: hidden;
}

.person-image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100%;
  object-position: center top;
  object-fit: cover;
}

.person-image-placeholder {
  --image-placeholder-padding: 1.2rem;
  --image-placeholder-background: white;
  --image-placeholder-logo-width: 72%;
  --image-placeholder-logo-max-width: 8rem;
  --image-placeholder-logo-opacity: 0.32;
  --image-placeholder-logo-blur: 1.2px;
}

.person-copy {
  display: grid;
  align-content: start;
  gap: 0.8rem;
  padding: 1rem 1.1rem 1.1rem;
  min-width: 0;
}

.person-role {
  margin: 0;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.74rem;
  font-weight: 700;
}

.person-name {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.08;
}

.person-name a {
  color: var(--color-text-strong);
}

.person-room {
  margin: 0;
  color: var(--color-text-muted);
}

.person-meta {
  display: grid;
  gap: 0.5rem;
}

.person-meta p {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0;
  min-width: 0;
  color: var(--color-text);
}

.person-meta a {
  min-width: 0;
  color: inherit;
  overflow-wrap: anywhere;
}

@media (max-width: 760px) {
  .person-card {
    grid-template-columns: 1fr;
  }

  .person-image-link {
    aspect-ratio: 4 / 5;
    height: auto;
  }

  .person-image {
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  .person-copy {
    padding-top: 0.9rem;
  }
}
</style>
